import posthog, {
  type EventName,
  type PostHog,
  type Properties,
} from "posthog-js"

import type { User } from "@/types"

import { sanitizeAnalyticsValue } from "./analytics-sanitizer"

const ANALYTICS_DISTINCT_ID_COOKIE = "cp_analytics_distinct_id"
// Keep the browser-written analytics cookie aligned with the Rails expiry.
const ANALYTICS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2

type AnalyticsUser = Pick<
  User,
  "id" | "name" | "email" | "created_at" | "verified" | "paid" | "admin"
>

let initialized = false
let trackingAllowed = false
let identifiedFingerprint: string | null = null
let analyticsProjectToken: string | null = null
let analyticsHost: string | null = null
const posthogClient: PostHog = posthog

function resetClientIdentity() {
  posthogClient.reset()
  posthogClient.register({ environment: "production", analytics_version: 2 })
}

function distinctIdFor(user: AnalyticsUser) {
  return `user_${user.id}`
}

function fingerprintFor(user: AnalyticsUser) {
  return JSON.stringify({
    id: user.id,
    email: user.email,
    name: user.name,
    verified: user.verified,
    paid: user.paid,
    admin: user.admin,
    created_at: user.created_at,
  })
}

function userRoleFor(user: AnalyticsUser) {
  if (user.admin) return "admin"
  if (user.paid) return "paid"
  return "free"
}

function canTrack() {
  return (
    trackingAllowed &&
    Boolean(analyticsProjectToken) &&
    typeof window !== "undefined"
  )
}

export function analyticsEnabled() {
  return canTrack()
}

function syncAnonymousDistinctIdCookie() {
  if (!initialized) return

  const distinctId = posthogClient.get_distinct_id()
  if (!distinctId || distinctId.startsWith("user_")) return

  const secure = window.location.protocol === "https:" ? "; secure" : ""
  document.cookie = `${ANALYTICS_DISTINCT_ID_COOKIE}=${encodeURIComponent(distinctId)}; path=/; max-age=${ANALYTICS_COOKIE_MAX_AGE}; samesite=lax${secure}`
}

export function initializeAnalytics(
  options: {
    enabled?: boolean
    userId?: number | null
    projectToken?: string | null
    host?: string | null
    bootstrapDistinctId?: string | null
  } = {},
) {
  if (options.enabled !== undefined) trackingAllowed = options.enabled
  const nextProjectToken = options.projectToken?.trim()
  const nextHost = options.host?.trim()

  if (
    nextProjectToken !== undefined &&
    nextProjectToken !== null &&
    nextProjectToken !== ""
  ) {
    analyticsProjectToken = nextProjectToken
  }

  if (nextHost !== undefined && nextHost !== null && nextHost !== "") {
    analyticsHost = nextHost
  }

  if (!canTrack() || initialized) return

  const bootstrapDistinctId = options.bootstrapDistinctId?.trim()
  const projectToken = analyticsProjectToken!

  posthogClient.init(projectToken, {
    api_host: analyticsHost ?? undefined,
    // Adopt PostHog's dated defaults deliberately so SDK behavior only changes
    // when we choose to update this pinned value.
    defaults: "2026-01-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
    before_send: (event) => {
      if (!canTrack()) return null
      if (event)
        event.properties = {
          ...(sanitizeAnalyticsValue(event.properties) as Properties),
          // PostHog adds its public ingestion token before this callback.
          // Restore only that configured token; nested secrets stay redacted.
          token: projectToken,
        }
      return event
    },
    loaded: (client) => {
      if (import.meta.env.DEV) client.debug()
    },
  })

  initialized = true
  // Reconcile persisted identity before capturing anything. Bootstrap must not
  // overwrite evidence that the previous session belonged to another account.
  const previousId = posthogClient.get_distinct_id()
  const wasIdentified =
    previousId?.startsWith("user_") ||
    posthogClient.get_property("is_authenticated") === true
  const expectedId = options.userId ? `user_${options.userId}` : null
  if (wasIdentified && previousId !== expectedId) {
    resetClientIdentity()
  } else if (!wasIdentified && bootstrapDistinctId) {
    posthogClient.register({
      distinct_id: bootstrapDistinctId,
      $device_id: bootstrapDistinctId,
    })
  }
  posthogClient.register({ environment: "production", analytics_version: 2 })
  syncAnonymousDistinctIdCookie()
}

export function captureAnalyticsEvent(
  event: EventName,
  properties: Properties = {},
) {
  initializeAnalytics()
  if (!canTrack() || !initialized) return

  try {
    syncSessionCookie()
    posthogClient.capture(event, properties)
  } catch {
    // Analytics must never interrupt the interaction being measured.
  }
}

export function capturePageView(properties: Properties = {}) {
  captureAnalyticsEvent("$pageview", properties)
}

export function identifyAnalyticsUser(user: AnalyticsUser) {
  initializeAnalytics()
  if (!canTrack() || !initialized) return

  const fingerprint = fingerprintFor(user)
  if (identifiedFingerprint === fingerprint) return

  const previousId = posthogClient.get_distinct_id()
  if (previousId?.startsWith("user_") && previousId !== distinctIdFor(user)) {
    resetClientIdentity()
  }
  posthogClient.identify(distinctIdFor(user), {
    email: user.email,
    name: user.name,
    verified: user.verified,
    paid: user.paid,
    admin: user.admin,
    signed_up_at: user.created_at,
  })

  posthogClient.register({
    is_authenticated: true,
    user_role: userRoleFor(user),
    verified: user.verified,
    paid: user.paid,
    admin: user.admin,
  })

  identifiedFingerprint = fingerprint
}

export function resetAnalyticsUser() {
  initializeAnalytics()
  if (!canTrack() || !initialized) return
  if (
    !identifiedFingerprint &&
    !posthogClient.get_distinct_id()?.startsWith("user_") &&
    posthogClient.get_property("is_authenticated") !== true
  )
    return

  resetClientIdentity()
  identifiedFingerprint = null
  syncAnonymousDistinctIdCookie()
}

export function analyticsSessionId() {
  if (!canTrack() || !initialized) return undefined
  return posthogClient.get_session_id()
}

function syncSessionCookie() {
  const id = analyticsSessionId()
  if (!id) return
  const secure = window.location.protocol === "https:" ? "; secure" : ""
  document.cookie = `cp_analytics_session_id=${encodeURIComponent(id)}; path=/; max-age=1800; samesite=lax${secure}`
}
